import { useMemo } from 'react';

import { DataFilterOperation, ProgramType } from 'types';

export const useGetAdminMenuItems = ({
  adminPayoutsAccess,
  adminDashboardAccess,
  adminTaskAccess,
  isLightReachQCSettingsAdmin,
  feocDcCalculator,
  incentivesManagement,
  enableQcChecklists,
  isLightReachCapitalMarketsAdmin,
  isBillingBlockerAdmin,
  isPalmettoUser,
  isBulkPaymentLinkAdmin,
  isLightReachSupport,
  enableAvlAdmin,
  isHardwareExceptionsAdmin = false,
  isUnderwritingAdmin = false,
  welcomeCallStipRulesEnabled = false,
  promosEnabled = false,
  isHarnessEnvironment = false,
  hasItcEligibilityProgramType,
}: {
  feocDcCalculator: boolean;
  adminPayoutsAccess: boolean;
  adminDashboardAccess: boolean;
  adminTaskAccess: boolean;
  isLightReachQCSettingsAdmin: boolean;
  incentivesManagement: boolean;
  enableQcChecklists: boolean;
  isLightReachCapitalMarketsAdmin: boolean;
  isBillingBlockerAdmin: boolean;
  // Also gates AVL viewing (any sabal user, not just isLightReachQCSettingsAdmin) —
  // AVL editing is further restricted server-side (and route-side) to canImportAvlData.
  isPalmettoUser: boolean;
  isBulkPaymentLinkAdmin: boolean;
  isLightReachSupport: boolean;
  enableAvlAdmin: boolean;
  // Optional-with-default: this is a deliberate exception to the hook's all-required convention,
  // to avoid touching every existing literal call site in useGetAdminMenuItems.test.tsx.
  isHardwareExceptionsAdmin?: boolean;
  isUnderwritingAdmin?: boolean;
  welcomeCallStipRulesEnabled?: boolean;
  promosEnabled?: boolean;
  // The accountctl API surface only mounts when SERVICE_ENV is local or next
  // (see registerLocalAndNextRoutes), so anywhere else the Test Account Generator
  // is a dead link. Defaults to false so a caller that forgets it hides the item.
  isHarnessEnvironment?: boolean;
  hasItcEligibilityProgramType: boolean;
}) => {
  return useMemo(
    () => [
      {
        name: 'Markets',
        path: `/admin/markets?advancedFilters=${encodeURIComponent(JSON.stringify([{ id: 'programType', operation: DataFilterOperation.Equal, selectedValues: [ProgramType.solar] }]))}`,
      },
      { name: 'Contracts', path: '/admin/contracts' },
      { name: 'Pricing', path: '/admin/pricing/solar' },
      ...(feocDcCalculator && hasItcEligibilityProgramType
        ? [
            {
              name: 'ITC Eligibility Calculator',
              path: '/admin/itc-eligibility-calculator',
            },
          ]
        : []),
      { name: 'Funds', path: '/admin/funds' },
      ...(adminPayoutsAccess ? [{ name: 'Payouts', path: '/admin/payouts/queued' }] : []),
      { name: 'Sales Reps', path: '/admin/sales-reps' },
      ...(adminDashboardAccess ? [{ name: 'Dashboard', path: '/admin/dashboard' }] : []),
      ...(adminTaskAccess ? [{ name: 'Tasks', path: '/admin/tasks' }] : []),
      ...(isLightReachQCSettingsAdmin
        ? [
            {
              name: 'Quality Control',
              path: '/admin/quality-control/requirement-flag-settings',
            },
          ]
        : []),
      ...(isLightReachQCSettingsAdmin && enableQcChecklists
        ? [{ name: 'Checklists', path: '/admin/checklists' }]
        : []),
      ...(isPalmettoUser && enableAvlAdmin
        ? [{ name: 'Approved Vendor List', path: '/admin/approved-vendors' }]
        : []),
      ...(isHardwareExceptionsAdmin ? [{ name: 'Hardware Exceptions', path: '/admin/hardware-exceptions' }] : []),
      ...(incentivesManagement ? [{ name: 'Incentives Management', path: '/admin/incentives' }] : []),
      ...(isLightReachCapitalMarketsAdmin ? [{ name: 'Document Pull', path: '/admin/document-pull' }] : []),
      ...(isBillingBlockerAdmin ? [{ name: 'Block Billing', path: '/admin/billing-blocks' }] : []),
      ...(isPalmettoUser && isHarnessEnvironment
        ? [
            { name: 'Test Account Generator', path: '/internal/accountctl' },
            { name: 'Conga Test Envelopes', path: '/internal/conga-test' },
          ]
        : []),
      ...(isBulkPaymentLinkAdmin ? [{ name: 'Bulk Payment Links', path: '/admin/bulk-payment-links' }] : []),
      ...(isLightReachSupport ? [{ name: 'Past Due Accounts', path: '/admin/past-due-accounts' }] : []),
      ...(isUnderwritingAdmin && welcomeCallStipRulesEnabled
        ? [
            {
              name: 'Welcome Call Stip Rules',
              path: '/admin/welcome-call-stip-rules',
            },
          ]
        : []),
      ...(promosEnabled ? [{ name: 'Promo Management', path: '/admin/promos' }] : []),
    ],
    [
      feocDcCalculator,
      adminPayoutsAccess,
      adminDashboardAccess,
      adminTaskAccess,
      isLightReachQCSettingsAdmin,
      incentivesManagement,
      enableQcChecklists,
      isLightReachCapitalMarketsAdmin,
      isBillingBlockerAdmin,
      isPalmettoUser,
      isBulkPaymentLinkAdmin,
      isLightReachSupport,
      enableAvlAdmin,
      isHardwareExceptionsAdmin,
      isUnderwritingAdmin,
      welcomeCallStipRulesEnabled,
      promosEnabled,
      isHarnessEnvironment,
      hasItcEligibilityProgramType,
    ],
  );
};
